import { useApi } from "@/hooks";
import { AppContext } from "@Context/common/general/context";
import { trainingPlansEvents } from "@Context/game/training-plans/training-plans-events";
import useI18n from "@i18n";
import { Api } from "@Shared/schemas";
import { useCallback, useContext, useEffect, useRef } from "react";
import {
  getCompletedSessionsCount,
  getRequiredSessionsCount,
  ITrainingPlanProgressData,
  loadStoredCompletedSessionsCount,
  storeCompletedSessionsCount,
} from "./training-plans-shared";

/**
 * Progress-toast trigger orchestrator for Training Plans.
 *
 * Responsibility:
 * - Detects relevant results-route transitions after a session.
 * - Refreshes progress snapshot and decides progress vs completion messaging.
 * - Emits delayed toast events through the Training Plans event bus.
 */
export const useTrainingPlansProgressToastTrigger = () => {
  const { state } = useContext(AppContext);
  const { i18n } = useI18n();
  const toastTimeoutRef = useRef<number | null>(null);

  const { call: getUserPlanProgress } = useApi<typeof Api.TrainingPlans.GetUserPlanProgress>({
    api: Api.TrainingPlans.GetUserPlanProgress,
    disableLoader: true,
  });

  const isAuthenticated = state.user.authenticated;
  const hasSelectedAccount = Boolean(state.account.id);
  const hasSubscription = state.user.hasAccessSubscriptions && !state.user.hasAllAccessExpired;
  const canUseTrainingPlans = isAuthenticated && hasSelectedAccount && hasSubscription;

  const refreshProgress = useCallback(async () => {
    return (await getUserPlanProgress({ include_history: false })) as ITrainingPlanProgressData | null;
  }, [getUserPlanProgress]);

  useEffect(() => {
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }

    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    };
  }, [state.account.id, state.user.id]);

  useEffect(() => {
    if (!canUseTrainingPlans) {
      return;
    }

    const storageParams = {
      accountId: state.account.id,
      userId: state.user.id,
    };
    const previousCompletedSessionsCount = loadStoredCompletedSessionsCount(storageParams);

    refreshProgress()
      .then(nextProgress => {
        const completedSessionsCount = getCompletedSessionsCount(nextProgress);
        const requiredSessionsCount = getRequiredSessionsCount(nextProgress);

        storeCompletedSessionsCount(storageParams, nextProgress);
        trainingPlansEvents.emitRefreshProgress();

        if (previousCompletedSessionsCount === null || completedSessionsCount <= previousCompletedSessionsCount || requiredSessionsCount === 0) {
          return;
        }

        if (toastTimeoutRef.current) {
          window.clearTimeout(toastTimeoutRef.current);
        }

        const isPlanComplete = completedSessionsCount >= requiredSessionsCount;
        toastTimeoutRef.current = window.setTimeout(() => {
          trainingPlansEvents.emitShowToast({
            isComplete: isPlanComplete,
            title: i18n(isPlanComplete ? "training-plans.toast.complete.title" : "training-plans.toast.progress.title"),
            message: isPlanComplete
              ? i18n("training-plans.toast.complete.message")
              : i18n("training-plans.toast.progress.message", {
                  completed: completedSessionsCount,
                  required: requiredSessionsCount,
                }),
          });
        }, 3000);
      })
      .catch(error => {
        console.error("Could not refresh training plan progress after session results", error);
      });
  }, [canUseTrainingPlans, i18n, refreshProgress, state.account.id, state.user.id]);
};
